import React from 'react';
import { omit, times } from 'ramda';
import { scroller } from 'react-scroll';
import { FormattedMessage } from 'react-intl';
import { parse } from 'query-string';
import { useRouter } from 'next/router';

import { ARTWORKS_LIMIT, ARTWORKS_PAGE_PREFIX } from '@modules/artworks';
import { getCurrentPLP } from '@modules/artworks/artworks.helpers';
import { SCROLL_DURATION } from '@components/PDP/pdp.constants';
import { useArtworksRoute } from '@hooks/useArtworks/useArtworks';
import {
  useFetchTradeArtworks,
  useFetchArtworks,
  useSetCurrentPage,
  useSetCurrentTradePage,
  useFetchGroupingArtworks,
  useSetCurrentGroupingPage,
} from '@hooks/useArtworks';

import { Container, Dots, PageCopy, PageNumber, PageNumbers } from './artworksPagination.styles';

export const ArtworksPagination = () => {
  const router = useRouter();
  const { currentPage: currentArtworksPage } = useSetCurrentPage();
  const { currentTradePage } = useSetCurrentTradePage() || {};
  const { fetchArtworks, meta: artworksMeta, loading: artworksLoading } = useFetchArtworks();
  const { fetchTradeArtworks, meta: tradeMeta, loading: tradeArtworksLoading } = useFetchTradeArtworks() || {};
  const { currentGroupingPage } = useSetCurrentGroupingPage() || {};
  const { fetchGroupingArtworks, groupingMeta, groupingLoading } = useFetchGroupingArtworks() || {};
  const { isGroupingRoute, isTradeMemberRoute } = useArtworksRoute();
  const fetch = isTradeMemberRoute ? fetchTradeArtworks : fetchArtworks;
  const currentPLP = getCurrentPLP();

  const allMeta = {
    groupings: groupingMeta,
    trade: tradeMeta,
    artworks: artworksMeta,
  };

  const allLoading = {
    groupings: groupingLoading,
    trade: tradeArtworksLoading,
    artworks: artworksLoading,
  };

  const allCurrentPage = {
    groupings: currentGroupingPage,
    trade: currentTradePage,
    artworks: currentArtworksPage,
  };

  const meta = allMeta[currentPLP];
  const loading = allLoading[currentPLP];
  const currentPage = allCurrentPage[currentPLP];

  const pageCount = Math.ceil(meta.count / ARTWORKS_LIMIT);

  const pages = times((index) => {
    if (
      (currentPage - 3 < index && index < currentPage + 1) ||
      (index > pageCount - 5 && currentPage > pageCount - 3) ||
      (index < 4 && currentPage < 3) ||
      !index ||
      pageCount - 1 === index
    ) {
      return index;
    }

    return '...';
  }, pageCount);

  const reducedPages = pages.reduce((prev, next) => {
    if (prev[prev.length - 1] === next) {
      return prev;
    }
    return [...prev, next];
  }, [] as (string | number)[]);

  const handleScrollToPage = async (page: number) => {
    const isPageExist = document.getElementsByName(`artworks-page-${page}`)[0];

    if (!isPageExist) {
      const query = parse(location.search.replace('?', ''));
      const limit = page * ARTWORKS_LIMIT;
      const parameters = {
        page,
        limit,
        offset: ARTWORKS_LIMIT,
        infiniteLoader: true,
        filterOptions: omit(['page'], query),
      };

      if (isGroupingRoute) {
        const slug = router.query.grouping as string;
        await fetchGroupingArtworks({
          ...parameters,
          slug,
        });
      } else {
        await fetch(parameters);
      }
    }

    setTimeout(() => {
      scroller.scrollTo(`${ARTWORKS_PAGE_PREFIX}${page}`, {
        duration: SCROLL_DURATION,
        smooth: true,
        offset: -50,
      });
    });
  };

  return (
    <Container>
      <PageCopy>
        <FormattedMessage id="artworksPagination.page" defaultMessage="Page" />
      </PageCopy>
      <PageNumbers>
        {reducedPages.map((page, index) =>
          typeof page === 'number' ? (
            <PageNumber
              key={index}
              onClick={() => handleScrollToPage(page + 1)}
              active={currentPage === page + 1}
              disabled={loading}
            >
              {page + 1}
            </PageNumber>
          ) : (
            <Dots key={index}>{page}</Dots>
          )
        )}
      </PageNumbers>
    </Container>
  );
};
